import React, { useState } from 'react';
import { CiLock } from "react-icons/ci";
import { IoIosEye, IoIosEyeOff } from 'react-icons/io';
import { twMerge } from 'tailwind-merge';
import clsx from "clsx";

const baseInputClass =
    "file:text-foreground placeholder:first-letter:uppercase placeholder:text-muted-foreground bg-[#FBFCFD]  border-primary flex w-full min-w-0 rounded-md border text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:border-0 file:bg-transparent file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

const variantClasses = {
    default:
        "focus-visible:border-primary  focus-visible:ring-primary/20 dark:focus-visible:ring-primary/40 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    error:
        "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 focus-visible:ring-[3px]",
    success:
        "border-green-500 focus-visible:border-green-500 focus-visible:ring-green-500/20 dark:focus-visible:ring-green-500/40 focus-visible:ring-[3px]",
    ghost:
        "border-transparent shadow-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
};

const sizeClasses = {
    sm: "h-8 py-1 text-sm",
    default: "h-9 py-1",
    lg: "h-12 py-2",
};


export interface IInputProps
    extends Omit<React.ComponentProps<"input">, "size"> {
    label?: string;
    error?: string;
    startContent?: React.ReactNode;
    endContent?: React.ReactNode;
    containerClassName?: string;
    secure?: boolean;
    labelClassName?: string;
    required?: boolean;
    variant?: keyof typeof variantClasses;
    size?: keyof typeof sizeClasses;
}


function Input({
    className,
    type = "text",
    label,
    error,
    startContent,
    endContent,
    variant = "default",
    size = "default",
    required,
    containerClassName,
    labelClassName,
    secure = false,
    ...props
}: IInputProps) {
    const [showPassword, setShowPassword] = useState(false);

    const effectiveVariant = error ? "error" : variant;

    const effectiveStartContent = secure ? (
        <CiLock className="w-6 h-6 text-primary" />
    ) : (
        startContent
    );

    const effectiveEndContent = secure ? (
        <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            className="hover:text-foreground transition-colors cursor-pointer"
        >
            {showPassword ? (
                <IoIosEyeOff className="h-6 w-6 text-primary" />
            ) : (
                <IoIosEye className="h-6 w-6 " />
            )}
        </button>
    ) : (
        endContent
    );

    return (
        <div className={twMerge("w-full", containerClassName)}>
            {label && (
                <label
                    className={twMerge(
                        "block text-sm font-medium text-foreground mb-2",
                        labelClassName
                    )}
                >
                    {label}
                    {required && (
                        <span className="text-red-600 text-sm font-medium"> *</span>
                    )}
                </label>
            )}

            <div className="relative">
                {effectiveStartContent && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        {effectiveStartContent}
                    </div>
                )}

                <input
                    type={
                        secure
                            ? showPassword
                                ? "text"
                                : "password"
                            : type
                    }
                    aria-invalid={!!error}
                    className={twMerge(
                        clsx(
                            baseInputClass,
                            variantClasses[effectiveVariant],
                            sizeClasses[size],
                            effectiveStartContent ? "pl-10" : "px-3",
                            effectiveEndContent ? "pr-10" : "pr-3",
                            "file:h-7 file:text-sm"
                        ),
                        className

                    )}
                    {...props}
                />

                {effectiveEndContent && (
                    <div className="absolute right-3 top-[60%] -translate-y-1/2 text-muted-foreground">
                        {effectiveEndContent}
                    </div>
                )}
            </div>

            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
}

export { Input };

